// Private (RAILGUN) activity row. Extracts the same fields as
// `common/.../Activity/PrivateSubmittedTransactionSummary` (category → kind,
// primary token amount, counterparty, status, fee, network, timestamp) but
// renders the Home v2 design card (ActivityRow) so private matches public.
import { formatUnits } from 'ethers'
import React, { useCallback, useMemo } from 'react'
import { useModalize } from 'react-native-modalize'

import { Network } from '@ambire-common/interfaces/network'
import { getUiType } from '@common/utils/uiType'
import PrivateSummaryDetailsSheet from '@common/modules/dashboard/components/Activity/PrivateSummaryDetailsSheet'
import useNavigation from '@common/hooks/useNavigation'
import { ROUTES } from '@common/modules/router/constants/common'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import { usePrivateGasFeePayment } from '@common/modules/dashboard/components/Activity/usePrivateGasFeePayment'
import { usePrivateTransactionReceipt } from '@common/modules/dashboard/components/Activity/usePrivateTransactionReceipt'
import { usePrivateTransactionStatus } from '@common/modules/dashboard/components/Activity/usePrivateTransactionStatus'
import { PrivateHistoryItem } from '@common/modules/dashboard/components/Activity/PrivateSubmittedTransactionSummary'
import {
  NETWORK_CONFIG,
  RailgunWalletBalanceBucket,
  TransactionHistoryItemCategory
} from '@railgun-community/shared-models'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import { DisplayBalanceChange } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/types'
import { useRailgunShieldTxLog } from '@web/modules/shield/hooks/useRailgunShieldTxLog'
import { getRailgunTxMessage } from '@web/utils/railgunTx'

import { ActivityKind } from '../tokens'
import useFeeFormatted from '@common/hooks/useFeeFormatted'
import useResolvedToken from '../useResolvedToken'
import ActivityRow from './ActivityRow'

const { isTab } = getUiType()

const KIND_BY_CATEGORY: Record<string, { kind: ActivityKind; label: string }> = {
  [TransactionHistoryItemCategory.ShieldERC20s]: { kind: 'shield', label: 'Shielded' },
  [TransactionHistoryItemCategory.UnshieldERC20s]: { kind: 'unshield', label: 'Unshielded' },
  [TransactionHistoryItemCategory.TransferSendERC20s]: { kind: 'send', label: 'Sent' },
  [TransactionHistoryItemCategory.TransferReceiveERC20s]: { kind: 'receive', label: 'Received' }
}

const PrivateActivityRow: React.FC<{ item: PrivateHistoryItem }> = ({ item }) => {
  const { networks } = useNetworksControllerState()
  const { navigate } = useNavigation()
  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()

  const network = useMemo<Network | undefined>(() => {
    const chainId = NETWORK_CONFIG[item.networkName]?.chain.id
    if (!chainId) return networks[0]
    return networks.find((n: Network) => Number(n.chainId) === chainId) || networks[0]
  }, [networks, item.networkName])

  const resolvedChainId =
    network?.chainId ?? BigInt(NETWORK_CONFIG[item.networkName]?.chain.id ?? 1n)

  const { kind, label } = KIND_BY_CATEGORY[item.category] || {
    kind: 'other' as ActivityKind,
    label: 'Private transaction'
  }
  const isReceive = item.category === TransactionHistoryItemCategory.TransferReceiveERC20s

  // A single txid can carry MANY ERC20 outputs (a Shield/receive produces one
  // commitment per UTXO change). Pick the primary token, then SUM every output
  // that shares its tokenAddress so the row shows the full received/sent amount,
  // not just the first commitment.
  const sentAmounts = [...item.transferERC20Amounts, ...item.unshieldERC20Amounts]
  const relevantAmounts = sentAmounts.length ? sentAmounts : item.receiveERC20Amounts
  const primary = relevantAmounts[0]
  const tokenAddress = primary?.tokenAddress
  const amountRaw = useMemo(() => {
    if (!tokenAddress) return undefined
    return relevantAmounts
      .filter((x) => x.tokenAddress?.toLowerCase() === tokenAddress.toLowerCase())
      .reduce((sum, x) => sum + BigInt(x.amount), 0n)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantAmounts, tokenAddress])

  const { symbol, decimals } = useResolvedToken(tokenAddress, resolvedChainId)
  const amount = useMemo(() => {
    if (amountRaw == null) return undefined
    if (decimals == null) return undefined
    return formatDecimals(parseFloat(formatUnits(amountRaw, decimals)), 'amount')
  }, [amountRaw, decimals])

  // For base-token unshields (e.g. WETH→ETH) the SDK history stores the Relay
  // Adapt contract as the unshield recipient, not the public address the user
  // typed. Prefer the recipient we persisted locally at broadcast time, keyed by
  // on-chain tx hash. See railgunContext.executeUnshieldFromProof.
  const { records: shieldTxLog } = useRailgunShieldTxLog()
  const isUnshield = item.category === TransactionHistoryItemCategory.UnshieldERC20s
  const recordedUnshieldRecipient = isUnshield
    ? shieldTxLog.find((r) => r.txHash?.toLowerCase() === item.txid.toLowerCase())?.recipientAddress
    : undefined

  const counterpartyRaw = isReceive
    ? item.receiveERC20Amounts.find((x) => !!x.senderAddress)?.senderAddress
    : recordedUnshieldRecipient ||
      ((item.transferERC20Amounts[0] as any)?.recipientAddress as string | undefined) ||
      ((item.unshieldERC20Amounts[0] as any)?.recipientAddress as string | undefined)
  const isPrivateCp = !counterpartyRaw || counterpartyRaw.trim().toLowerCase() === 'private address'
  const counterpartyAddress = isPrivateCp ? undefined : shortenAddress(counterpartyRaw!, 18)

  // Raw (un-shortened) counterparty + flags fed to the detail sheet, which shows
  // the full address and decides EVM-vs-RAILGUN rendering itself.
  const counterpartyRawFull = counterpartyRaw || 'Private address'
  const isEvmAddress = counterpartyRawFull.toLowerCase().startsWith('0x')
  const amountText = amountRaw != null ? amountRaw.toString() : ''

  const { receipt, isLoading: isReceiptLoading } = usePrivateTransactionReceipt({
    txid: item.txid,
    networkName: item.networkName,
    networkChainId: resolvedChainId
  })
  const { gasFeePayment, isFeeLoading } = usePrivateGasFeePayment({
    item,
    networkChainId: resolvedChainId,
    receipt,
    isReceiptLoading
  })
  const { status, balanceBucket, validPOI } = usePrivateTransactionStatus({ item })
  const { feeText, isLoading: feeFormatting } = useFeeFormatted(gasFeePayment, network)

  // Pending estimation / POI status line — same source the extension activity
  // row uses (getRailgunTxMessage). Restores the "Est. ~X min remaining for
  // your Private Proof of Innocence" / "Waiting for Private Proof of Innocence"
  // / "Completing soon" copy that the Home v2 row otherwise dropped.
  const statusMessage = getRailgunTxMessage({
    txid: item.txid,
    timestamp: item.timestamp,
    networkName: item.networkName,
    balanceBucket,
    savedTxLog: shieldTxLog,
    poiPending: !validPOI,
    category: item.category
  })

  // ShieldBlocked is a POI-layer state (shield confirmed on-chain, but the POI
  // list blocked the commitment), NOT an on-chain failure — surface it as an
  // amber "Blocked" pill with a PPOI.info deep link instead of red "Failed".
  // `item.networkName` is the RAILGUN NetworkName (e.g. "BNB_Chain"), which is
  // already the exact ppoi.info path segment.
  const isBlocked = balanceBucket === RailgunWalletBalanceBucket.ShieldBlocked
  const ppoiUrl = isBlocked ? `https://ppoi.info/${item.networkName}/tx/${item.txid}` : undefined

  // Blocked shields can be recovered to their origin public address. Deep-link to
  // the confirm screen, carrying the original shield txid via navigation state.
  const onUnshieldToOrigin = useCallback(() => {
    navigate(ROUTES.unshieldToOrigin, {
      state: {
        originalShieldTxid: item.txid,
        chainId: resolvedChainId.toString(),
        tokenAddress,
        networkName: item.networkName
      }
    })
  }, [navigate, item.txid, item.networkName, resolvedChainId, tokenAddress])

  // Balance changes for the detail sheet, in the same shape the public sheet
  // feeds BalanceChangesSection. A private txn moves exactly one token in or out
  // of the RAILGUN balance: receives/shields are "in", sends/unshields are "out".
  // The network fee is paid publicly and gets its own row, so it stays out of here.
  const isIncoming = isReceive || item.category === TransactionHistoryItemCategory.ShieldERC20s
  const balanceChanges = useMemo<DisplayBalanceChange[]>(() => {
    if (!tokenAddress || amountRaw == null || decimals == null) return []

    const signedAmount = isIncoming ? amountRaw : -amountRaw

    return [
      {
        address: tokenAddress,
        symbol: symbol || '',
        name: symbol || '',
        decimals,
        chainId: resolvedChainId,
        priceIn: [],
        marketDataIn: [],
        flags: {} as any,
        amount: amountRaw,
        amountBefore: 0n,
        amountAfter: amountRaw,
        balanceChange: signedAmount
      } as DisplayBalanceChange
    ]
  }, [tokenAddress, amountRaw, decimals, symbol, resolvedChainId, isIncoming])

  // Humanized "call" for the detail sheet's TransactionSummary — mirrors the
  // legacy PrivateSubmittedTransactionSummary so the popup detail view matches.
  const call = useMemo(
    () =>
      ({
        id: item.txid,
        to: isPrivateCp || !isEvmAddress ? undefined : counterpartyRawFull,
        value: 0n,
        data: undefined,
        fullVisualization: [
          { id: `${item.txid}-action`, type: 'action', content: label },
          tokenAddress && amountText
            ? { id: `${item.txid}-token`, type: 'token', value: amountText, address: tokenAddress }
            : null,
          !isPrivateCp
            ? {
                id: `${item.txid}-label-counterparty`,
                type: 'label',
                content: isReceive ? 'from' : 'to'
              }
            : null,
          !isPrivateCp && counterpartyRawFull && isEvmAddress
            ? { id: `${item.txid}-recipient`, type: 'address', address: counterpartyRawFull }
            : null
        ].filter(Boolean)
      }) as any,
    [
      item.txid,
      isPrivateCp,
      isEvmAddress,
      counterpartyRawFull,
      label,
      tokenAddress,
      amountText,
      isReceive
    ]
  )

  if (!network) return null

  return (
    <>
      <ActivityRow
        data={{
          id: item.txid,
          kind,
          kindLabel: label,
          amount,
          tokenSymbol: symbol,
          tokenAddress,
          chainId: resolvedChainId,
          counterpartyLabel: isReceive ? 'from' : 'to',
          counterpartyAddress,
          status,
          isBlocked,
          ppoiUrl,
          onUnshieldToOrigin: isBlocked ? onUnshieldToOrigin : undefined,
          statusMessage: statusMessage ?? undefined,
          feeText,
          isFeeLoading: isFeeLoading || feeFormatting,
          networkName: network.name,
          timestampMs: (item.timestamp ?? 0) * 1000,
          explorerUrl: network.explorerUrl,
          txHash: item.txid,
          onPress: openBottomSheet
        }}
      />
      {/* Tap the row → private detail sheet (same one the legacy popup used). */}
      <PrivateSummaryDetailsSheet
        sheetRef={sheetRef}
        closeBottomSheet={closeBottomSheet}
        modalType={isTab ? 'modal' : 'bottom-sheet'}
        network={network}
        size="md"
        call={call}
        chainId={resolvedChainId}
        txid={item.txid}
        isPrivateCounterparty={isPrivateCp}
        isEvmAddress={isEvmAddress}
        counterpartyAddress={counterpartyRawFull}
        extraMessage={statusMessage}
        status={status}
        balanceChanges={balanceChanges}
        feeText={feeText}
        isFeeLoading={isFeeLoading || feeFormatting}
        timestamp={(item.timestamp ?? 0) * 1000}
        isBlocked={isBlocked}
        ppoiUrl={ppoiUrl}
        onUnshieldToOrigin={isBlocked ? onUnshieldToOrigin : undefined}
      />
    </>
  )
}

export default React.memo(PrivateActivityRow)
